import { COLORS } from "rylak-common/src/constants";
import { COMPLAINT_STATUS, QUERY_MAP, ROUTES, ROUTES_VALUES_WITHOUT_QUERY, USER_ROLES } from "../constants";
import {
    faBarcode,
    faBox,
    faCartArrowDown,
    faClipboardCheck,
    faClock,
    faCloudUpload,
    faCog,
    faCommentQuestion,
    faEnvelope,
    faEnvelopesBulk,
    faExclamationTriangle,
    faFrownOpen,
    faHammer,
    faHand,
    faHandHoldingUsd,
    faHome,
    faKey,
    faMoneyCheckAlt,
    faNetworkWired,
    faPalette,
    faSyncAlt,
    faTag,
    faTelescope,
    faText,
    faTruck,
    faTshirt,
    faUser,
    faUsers,
    faWarehouseFull,
    faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import React from "react";
import {
    getLoggedInUserDisplayName,
    getUserRole,
    isRoleAdminOrGreater,
    isRoleShipment,
    isRoleSuperAdmin,
    isRoleSupportOrGreater,
    isRoleWarehouse,
    isUserTypeIngrid,
    isUserTypeShopOrGreater,
    isUserTypeShopShipmentCarrier,
} from "./appCore";
import { stringHasAtLeastXSlashes } from "rylak-common";
import { getOrderItemProduct } from "./orderCore";

export function getDesignSettingsPageData() {
    return {
        href: ROUTES.SETTINGS_APP_DESIGN,
        icon: faPalette,
        title: "App Design",
        accessFn: accessFnShopAdmins,
    };
}

export function getReturnReasonsPageData() {
    return {
        href: ROUTES.SETTINGS_RETURN_REASONS,
        icon: faCommentQuestion,
        title: "Return Reason Setups",
        accessFn: accessFnShopAdmins,
    };
}

export function getShopSettingsPageData({ title = "Shop", subRoutes } = {}) {
    return {
        href: ROUTES.SETTINGS_SHOP,
        icon: faCog,
        title: title,
        accessFn: accessFnShopAdmins,
        subRoutes: subRoutes,
    };
}

export function getTextOverridesPageData() {
    return {
        href: ROUTES.SETTINGS_TEXT_OVERRIDES,
        icon: faText,
        title: "Text Overrides",
        accessFn: accessFnShopAdmins,
    };
}

export function getPriceSetupsPageData({ title = "Price Setups", subRoutes } = {}) {
    return {
        href: ROUTES.SETTINGS_PRICE_SETUPS,
        icon: faCog,
        title: title,
        accessFn: accessFnShopAdmins,
        subRoutes: subRoutes,
    };
}

export function getReturnsPageData({ title = "All", subRoutes } = {}) {
    return {
        href: ROUTES.RETURNS,
        icon: faTruck,
        flip: "horizontal",
        title: title,
        initiallyExpanded: false,
        accessFn: accessFnShopSupport,
        subRoutes: subRoutes,
    };
}

export function getQualityCheckPageData() {
    return {
        href: ROUTES.WAREHOUSE_QUALITY_CHECK,
        icon: faClipboardCheck,
        title: "Quality check",
        accessFn: accessFnShopSupportOrWarehouse,
    };
}

export function getWarehouseRemarksPageData() {
    return {
        href: ROUTES.WAREHOUSE_REQUIRE_ACTION,
        queryParams: { [QUERY_MAP.COMPLAINT_STATUS]: COMPLAINT_STATUS.PENDING },
        icon: faHand,
        title: "Require action",
        accessFn: accessFnShopSupport,
    };
}

export function getUserSettingsPageData(appProps, { style, showUserType = true } = {}) {
    const userRole = getUserRole(appProps);
    const displayNameType = (
        <div
            style={{
                color: USER_ROLES[userRole]?.color || COLORS.SUCCESS,
            }}
        >
            {USER_ROLES[userRole]?.name}
        </div>
    );

    return {
        href: ROUTES.SETTINGS_USER_SETTINGS,
        icon: faUser,
        title: (
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gridGap: "6px",
                    ...style,
                }}
            >
                <div style={{ whiteSpace: "nowrap" }}>{getLoggedInUserDisplayName(appProps) || "User Settings"}</div>
                {showUserType && <div style={{ whiteSpace: "nowrap" }}>{displayNameType}</div>}
            </div>
        ),
        accessFn: accessFnEveryone,
    };
}

export function getEcomIntegrationPageData({ title = "Ecom Integration", icon = faTruck, flip, subRoutes } = {}) {
    return {
        href: ROUTES.INTEGRATIONS_ECOM,
        icon: icon,
        flip: flip,
        title: title,
        accessFn: accessFnShopAdmins,
        subRoutes: subRoutes,
    };
}

export function getShipmentIntegrationsPageData({ title = "Shipment Integrations", icon = faTruck, flip, subRoutes } = {}) {
    return {
        href: ROUTES.INTEGRATIONS_SHIPMENT,
        icon: icon,
        flip: flip,
        title: title,
        accessFn: accessFnShopAdmins,
        subRoutes: subRoutes,
    };
}

export function getWarehouseIntegrationsPageData() {
    return {
        href: ROUTES.INTEGRATIONS_WAREHOUSE,
        icon: faWarehouseFull,
        title: "Warehouse Integration",
        accessFn: accessFnShopAdmins,
    };
}

export function getComplaintsPageData() {
    return {
        href: ROUTES.RETURNS_COMPLAINTS,
        queryParams: { [QUERY_MAP.COMPLAINT_STATUS]: COMPLAINT_STATUS.PENDING },
        icon: faFrownOpen,
        title: "Complaints",
        accessFn: accessFnShopSupport,
    };
}

export function getShipmentsPageData({ title = "Shipments", subRoutes } = {}) {
    return {
        href: ROUTES.RETURNS_SHIPMENTS,
        icon: faTruck,
        title: title,
        accessFn: accessFnShopSupportOrShipment,
        subRoutes,
    };
}

export function getShipmentIssuesPageData() {
    return {
        href: ROUTES.RETURNS_SHIPMENT_ISSUES,
        icon: faTruck,
        title: "Issues",
        accessFn: accessFnShopSupportOrShipment,
    };
}

export function getBagShipmentsPageData() {
    return {
        href: ROUTES.BAG_SHIPMENTS,
        icon: faBarcode,
        title: "Bag Shipments",
        accessFn: accessFnShipment,
    };
}

export function getShipmentLabelPageData() {
    return {
        href: ROUTES.SHIPMENT_LABEL,
        icon: faTag,
        title: "Search Shipment Label",
        accessFn: accessFnShipment,
    };
}

export function getOrdersActionPageData() {
    return {
        href: ROUTES.RETURNS_ACTIONS,
        icon: faHammer,
        title: "Action needed",
    };
}

export function getActiveReturnsPageData() {
    return {
        href: ROUTES.RETURNS_ACTIVE,
        icon: faWavePulse,
        title: "Active",
    };
}

export function getExchangesPageData() {
    return {
        href: ROUTES.RETURNS_EXCHANGES,
        icon: faSyncAlt,
        title: "Exchanges",
        accessFn: accessFnShopSupport,
    };
}

export function getResalesPageData() {
    return {
        href: ROUTES.RETURNS_RESALES,
        icon: faCartArrowDown,
        title: "Resales",
        accessFn: accessFnRoleSuperAdmin,
    };
}

export function getRefundsPageData({ title = "Refunds", subRoutes } = {}) {
    return {
        href: ROUTES.RETURNS_REFUNDS,
        icon: faHandHoldingUsd,
        title: title,
        accessFn: accessFnShopSupport,
        subRoutes: subRoutes,
    };
}

export function getRefundIssuesPageData() {
    return {
        href: ROUTES.RETURNS_REFUND_ISSUES,
        icon: faHandHoldingUsd,
        title: "Issues",
        accessFn: accessFnShopSupport,
    };
}

export function getUploadShipmentLabelPageData() {
    return {
        href: ROUTES.RETURNS_UPLOAD_LABEL,
        icon: faCloudUpload,
        title: "Label Upload",
        accessFn: accessFnShopSupport,
    };
}

export function getReturnDelaysPageData() {
    return {
        href: ROUTES.RETURNS_DELAYS,
        icon: faClock,
        title: "Delays",
        accessFn: accessFnShopSupport,
    };
}

export function getReturnIssuesPageData() {
    return {
        href: ROUTES.RETURNS_ISSUES,
        icon: faExclamationTriangle,
        title: "Issues",
        accessFn: accessFnShopSupport,
    };
}

export function getProductsPageData({ title = "Product insights", subRoutes } = {}) {
    return {
        href: ROUTES.PRODUCTS,
        icon: faTshirt,
        title: title,
        accessFn: accessFnShopSupport,
        subRoutes: subRoutes,
    };
}

export function getProductCategoriesPageData({ subRoutes } = {}) {
    return {
        href: ROUTES.PRODUCT_CATEGORIES,
        icon: faTshirt,
        title: "Categories",
        accessFn: accessFnShopSupport,
        subRoutes: subRoutes,
    };
}

export function getProductPoliciesPageData({ subRoutes } = {}) {
    return {
        href: ROUTES.PRODUCT_POLICIES,
        icon: faTshirt,
        title: "Policies",
        accessFn: accessFnShopSupport,
        subRoutes: subRoutes,
    };
}

export function getWarehouseIncomingItemsPageData({ title = "All", subRoutes } = {}) {
    return {
        href: ROUTES.WAREHOUSE_REPORTS,
        icon: faBox,
        initiallyExpanded: false,
        title: title,
        accessFn: accessFnShopSupport,
        subRoutes,
    };
}

export function getWarehouseIssuesPageData() {
    return {
        href: ROUTES.WAREHOUSE_ISSUES,
        queryParams: { [QUERY_MAP.FILTER_ERROR]: true },
        icon: faExclamationTriangle,
        title: "Issues",
        accessFn: accessFnShopSupport,
    };
}

export function getCustomersPageData() {
    return {
        href: ROUTES.CUSTOMERS,
        icon: faUsers,
        title: "Customers",
        accessFn: accessFnShopSupport,
    };
}

export function getBillingPageData() {
    return {
        href: ROUTES.BILLING,
        icon: faMoneyCheckAlt,
        title: "Billing & Invoices",
        accessFn: accessFnShopAdmins,
    };
}

export function getEmailTemplatePageData() {
    return {
        href: ROUTES.SETTINGS_EMAIL_TEMPLATES,
        icon: faEnvelope,
        title: "Email Templates",
        accessFn: accessFnShopAdmins,
    };
}

export function getApiKeysPageData() {
    return {
        href: ROUTES.SETTINGS_API_KEYS,
        icon: faKey,
        title: "API keys",
        accessFn: accessFnShopAdmins,
    };
}

export function getAdminSettingsPageData() {
    return {
        href: ROUTES.SETTINGS_ADMIN_SETTINGS,
        icon: faHammer,
        title: "Admin",
        accessFn: accessFnAdmins,
    };
}

export function getWebhookNotificationsPageData() {
    return {
        href: ROUTES.INTEGRATIONS_WEBHOOK_NOTIFICATIONS,
        icon: faEnvelopesBulk,
        title: "Incoming Notifications",
        accessFn: accessFnShopAdmins,
    };
}

export function getOutgoingWebhookNotificationsPageData() {
    return {
        href: ROUTES.INTEGRATIONS_WEBHOOK_NOTIFICATIONS_OUTGOING,
        icon: faEnvelopesBulk,
        title: "Outgoing Notifications",
        accessFn: accessFnShopAdmins,
    };
}

export function getWebhookSubscriptionsPageData() {
    return {
        href: ROUTES.INTEGRATIONS_WEBHOOK_OUTGOING_SUBSCRIPTIONS,
        icon: faEnvelopesBulk,
        title: "Webhook Subscriptions",
        accessFn: accessFnShopAdmins,
    };
}

export function getEntityEventsPageData() {
    return {
        href: ROUTES.ENTITY_EVENTS,
        icon: faWavePulse,
        title: "Entity events",
        accessFn: accessFnIngrid,
    };
}

export function getEntityExplorerPageData() {
    return {
        href: ROUTES.ENTITY_EXPLORER,
        icon: faTelescope,
        title: "Entity explorer",
        accessFn: accessFnIngrid,
    };
}

export function getDebugPageData({ subRoutes } = {}) {
    return {
        href: ROUTES.ENTITY_EXPLORER,
        icon: faTelescope,
        title: "Debug",
        accessFn: accessFnIngrid,
        subRoutes,
    };
}

function accessFnShipment(appProps) {
    return isRoleShipment(appProps) || isUserTypeShopShipmentCarrier(appProps) || isRoleSuperAdmin(appProps) || (isUserTypeIngrid(appProps) && isRoleSupportOrGreater(appProps));
}

function accessFnShopSupport(appProps) {
    return isRoleSupportOrGreater(appProps) && isUserTypeShopOrGreater(appProps);
}

function accessFnShopSupportOrWarehouse(appProps) {
    return (isRoleSupportOrGreater(appProps) || isRoleWarehouse(appProps)) && isUserTypeShopOrGreater(appProps);
}

function accessFnShopSupportOrShipment(appProps) {
    return (isRoleSupportOrGreater(appProps) || isRoleShipment(appProps)) && (isUserTypeShopOrGreater(appProps) || isUserTypeShopShipmentCarrier(appProps));
}

function accessFnShopAdmins(appProps) {
    return accessFnAdmins(appProps) && isUserTypeShopOrGreater(appProps);
}

function accessFnAdmins(appProps) {
    return isRoleAdminOrGreater(appProps);
}

function accessFnRoleSuperAdmin(appProps) {
    return isRoleSuperAdmin(appProps);
}

function accessFnIngrid(appProps) {
    return isUserTypeIngrid(appProps);
}

function accessFnEveryone() {
    return true;
}

export function getRouteItems(appProps) {
    return [
        {
            href: ROUTES.MAIN,
            icon: faHome,
            title: "Home",
            accessFn: accessFnShopSupport,
        },
        getProductsPageData({
            title: "Products",
            subRoutes: [getProductsPageData(), getProductCategoriesPageData(), getProductPoliciesPageData()],
        }),

        getReturnsPageData({
            title: "Returns",
            subRoutes: [
                getReturnsPageData(appProps),
                getUploadShipmentLabelPageData(appProps),
                getComplaintsPageData(appProps),
                getReturnDelaysPageData(appProps),
                getReturnIssuesPageData(appProps),
                getExchangesPageData(appProps),
                getResalesPageData(appProps),
            ],
        }),
        getShipmentsPageData({
            subRoutes: [getShipmentsPageData({ title: "All", appProps }), getShipmentIssuesPageData({ appProps })],
        }),
        getRefundsPageData({
            subRoutes: [getRefundsPageData({ title: "All", appProps }), getRefundIssuesPageData({ appProps })],
        }),
        getWarehouseIncomingItemsPageData({
            title: "Warehouse reports",
            subRoutes: [getWarehouseIncomingItemsPageData(), getWarehouseRemarksPageData(), getWarehouseIssuesPageData()],
        }),
        getQualityCheckPageData(),
        getBagShipmentsPageData(),
        getShipmentLabelPageData(),
        // {
        //     href: ROUTES.TURNR_REPACK,
        //     icon: faBarcode,
        //     title: "Turnr Repack",
        // },
        getCustomersPageData(),
        getBillingPageData(),
        getShopSettingsPageData({
            title: "Settings",
            subRoutes: [
                getShopSettingsPageData(),
                getPriceSetupsPageData(),
                getReturnReasonsPageData(),
                getDesignSettingsPageData(),
                getEmailTemplatePageData(),
                getTextOverridesPageData(),
                getAdminSettingsPageData(),
                getUserSettingsPageData(appProps, { showUserType: false }),
            ],
        }),
        getEcomIntegrationPageData({
            icon: faNetworkWired,
            flip: "horizontal",
            title: "Integrations",
            subRoutes: [
                getEcomIntegrationPageData(),
                getShipmentIntegrationsPageData(),
                getWarehouseIntegrationsPageData(),
                getWebhookSubscriptionsPageData(),
                getApiKeysPageData(),
                getWebhookNotificationsPageData(),
                getOutgoingWebhookNotificationsPageData(),
            ],
        }),
        getDebugPageData({
            subRoutes: [getEntityExplorerPageData(), getEntityEventsPageData()],
        }),
    ];

    // list.forEach(item => {
    //     if (guidePaths.includes(item.href)) {
    //         if (isStartupGuideActive(props) && !isStartupGuideDone(props)) {
    //             item.alertInfo = ({backgroundColor}) => <GuideAlertInfo backgroundColor={backgroundColor}/>
    //         }
    //     }
    // })
}

export function getActiveMainOrSubRouteItem(appProps) {
    return getRouteItems(appProps).reduce((routeResult, mainRouteItem) => {
        if (routeResult) {
            return routeResult;
        }
        const activeSubRouteItem = mainRouteItem.subRoutes?.find((subRouteItem) => {
            return isRouteItemActive({
                routeItem: subRouteItem,
                pathname: appProps.pathname,
            });
        });

        if (activeSubRouteItem) {
            return activeSubRouteItem;
        }

        if (
            isRouteItemActive({
                routeItem: mainRouteItem,
                pathname: appProps.pathname,
            })
        ) {
            return mainRouteItem;
        }
        return null;
    }, null);
}

export function removeLastIdPart(path) {
    return path.replace(/\/[^/]+$/, "");
}

export function isRouteItemActive({ routeItem, pathname }) {
    const href = routeItem.href;

    let pathNameWithoutQuery = pathname?.split("?")[0];

    let hrefWithoutQuery = href.split("?")[0];

    if (hrefWithoutQuery === "/") {
        return hrefWithoutQuery === pathNameWithoutQuery;
    }
    if (pathNameWithoutQuery === hrefWithoutQuery) {
        return true;
    }

    if (!ROUTES_VALUES_WITHOUT_QUERY.includes(pathNameWithoutQuery) && stringHasAtLeastXSlashes(pathNameWithoutQuery)) {
        pathNameWithoutQuery = removeLastIdPart(pathNameWithoutQuery);
    }
    if (stringHasAtLeastXSlashes(pathNameWithoutQuery, { nrOfSlashes: 3 })) {
        pathNameWithoutQuery = pathname.split("/").slice(0, -1).join("/");
    }

    return pathNameWithoutQuery === hrefWithoutQuery;
}

export function filterRouteItemsWithAccess(appProps, routeItems) {
    const result = routeItems?.reduce((filteredRouteItems, routeItem) => {
        const hasAccess = routeItem?.accessFn(appProps);
        if (routeItem?.subRoutes) {
            const filteredSubRoutes = routeItem?.subRoutes?.filter((subRoute) => subRoute?.accessFn(appProps));
            if (filteredSubRoutes?.length === 1) {
                return filteredRouteItems?.concat(filteredSubRoutes[0]);
            }
            if (filteredSubRoutes?.length > 1) {
                return filteredRouteItems?.concat({
                    ...routeItem,
                    ...(!hasAccess && { href: undefined }),
                    subRoutes: filteredSubRoutes,
                });
            }
        }
        if (hasAccess) {
            return filteredRouteItems?.concat(routeItem);
        }
        return filteredRouteItems;
    }, []);
    return result;
}

export function getTurnrOrderHref({ order, orderId, id }) {
    return getRouteHref(ROUTES.RETURN, { id: id || orderId || order?.id });
}

export function getTurnrCustomerHref({ id, customer }) {
    return getRouteHref(ROUTES.CUSTOMER, { id: id || customer?.id });
}

export function getTurnrProductCategoryHref({ id, category }) {
    return getRouteHref(ROUTES.PRODUCT_CATEGORY, { id: id || category?.id });
}

export function getTurnrProductHref({ orderItem, product = getOrderItemProduct(orderItem), productId, id }) {
    return getRouteHref(ROUTES.PRODUCT, { id: id || productId || product?.id });
}

export function getTurnrShipmentHref({ shipment, id = shipment?.id }) {
    return getRouteHref(ROUTES.RETURNS_SHIPMENT, { id: id });
}

export function getTurnrQualityCheckHref({ orderReturn }) {
    return getRouteHref(ROUTES.WAREHOUSE_QUALITY_CHECK, null, {
        [QUERY_MAP.SEARCH_STRING]: orderReturn?.order?.foreignId,
    });
}

export function getDesignSettingsDetailsHref({ detailsId }) {
    return getRouteHref(ROUTES.SETTINGS_APP_DESIGN, null, {
        [QUERY_MAP.APP_DESIGN_FORM_DETAILS]: detailsId,
    });
}

export function getReturnReasonSetupsHref() {
    return getRouteHref(ROUTES.SETTINGS_RETURN_REASONS);
}

export function getShopSettingsHref() {
    return getRouteHref(ROUTES.SETTINGS_SHOP);
}

export function getShopSettingsIngridHref() {
    return getRouteHref(ROUTES.SETTINGS_SHOP, null, { [QUERY_MAP.SELECTED_ENTITY_TAB]: "Ingrid" });
}

export function getShopSettingsQualityCheckHref() {
    return getRouteHref(ROUTES.SETTINGS_SHOP, null, { [QUERY_MAP.SELECTED_ENTITY_TAB]: "Quality check" });
}

export function getRouteHref(route, options, queryParams) {
    let returnHref = !options
        ? route
        : Object.keys(options).reduce((href, key) => {
              return href.replace("[" + key + "]", options[key]);
          }, route);

    if (queryParams) {
        const searchParams = new URLSearchParams();
        Object.keys(queryParams).forEach((key) => {
            searchParams.append(key, queryParams[key]);
        });
        returnHref += "?" + searchParams.toString();
    }
    return returnHref;
}
